import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { ArrowDown, ArrowUp, Loader2, Minus, Plus } from 'lucide-react';
import { ListFilterBar } from '../ui/ListFilterBar';
import { ListHeader } from '../ui/ListHeader';
import { FilterSelect } from '../ui/FilterSelect';
import { Checkbox } from '../ui/Checkbox';
import { CellMono } from '../ui/CellMono';
import { cn } from '../../lib/utils';
import { toast } from '../../lib/toast';
import { errorMessage } from '../../lib/errorMessage';
import type { ServiceInfo } from '../../lib/types';
import type { PendingScaleMap } from '../../hooks/usePendingScale';

type SortField = 'name' | 'stack' | 'system';
type SortDir = 'asc' | 'desc';

function uniqSorted(values: string[]): string[] {
  return Array.from(new Set(values.filter(Boolean))).sort((a, b) =>
    a.localeCompare(b, 'pt-BR', { sensitivity: 'base' })
  );
}

export interface ServiceListProps {
  services: ServiceInfo[];
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  filter: string;
  onFilterChange: (value: string) => void;
  bulk: Set<string>;
  onToggleBulk: (id: string) => void;
  onToggleBulkAll: (ids: string[], shouldSelectAll: boolean) => void;
  onQuickScale?: (
    service: ServiceInfo,
    replicas: number,
    from: number
  ) => Promise<unknown>;
  // serviceID -> IPs macvlan das tasks; permite achar o serviço digitando o
  // IP do container na mesma busca de nome.
  ipsByService?: Map<string, string[]>;
  compact?: boolean;
  pendingScale?: PendingScaleMap;
  accent?: string;
}

const COLS: Array<number | string> = [20, '1fr', 100, 78, 50, 50];
const QUICK_SCALE_COL = 96;

// Cliques seguidos no −/+ acumulam num rascunho local e viram UM POST /api/scale
// depois desta pausa — cada update de réplicas recria tasks no Swarm, então
// 3 cliques não podem ser 3 updates.
const QUICK_SCALE_FLUSH_MS = 600;

type ServiceStatus = 'zerado' | 'running' | 'degradado';

export function serviceStatus(s: ServiceInfo): ServiceStatus {
  if (s.machines !== s.replicas) return 'degradado';
  return s.replicas === 0 ? 'zerado' : 'running';
}

const STATUS_BADGE: Record<ServiceStatus, { label: string; className: string }> =
  {
    zerado: { label: 'ZERADO', className: 'bg-surfaceAlt text-textMute' },
    running: { label: 'RUNNING', className: 'bg-okBg text-ok' },
    degradado: { label: 'DEGRADADO', className: 'bg-dangerBg text-danger' },
  };

function filterServices(
  list: ServiceInfo[],
  filter: string,
  ipsByService?: Map<string, string[]>
): ServiceInfo[] {
  if (!filter) return list;
  let regex: RegExp | null = null;
  try {
    regex = new RegExp(filter, 'i');
  } catch {
    regex = null;
  }
  const lower = filter.toLowerCase();
  return list.filter(s => {
    if (regex ? regex.test(s.name) : s.name.toLowerCase().includes(lower)) {
      return true;
    }
    const ips = ipsByService?.get(s.id);
    return ips ? ips.some(ip => ip.includes(filter.trim())) : false;
  });
}

export function ServiceList({
  services,
  selectedId,
  onSelect,
  filter,
  onFilterChange,
  bulk,
  onToggleBulk,
  onToggleBulkAll,
  onQuickScale,
  ipsByService,
  compact,
  pendingScale = {},
}: ServiceListProps) {
  const [fila, setFila] = useState<string | null>(null);
  const [sistema, setSistema] = useState<string | null>(null);
  const [tipo, setTipo] = useState<string | null>(null);
  const searching = filter.trim() !== '';
  const [onlyScaled, setOnlyScaled] = useState(!searching);
  const [wasSearching, setWasSearching] = useState(searching);
  // Buscar tem de achar serviço ainda zerado (escala inicial): entrar e sair
  // da busca repõe o padrão; no meio dela vale o que o operador escolheu.
  if (searching !== wasSearching) {
    setWasSearching(searching);
    setOnlyScaled(!searching);
  }
  const [sortField, setSortField] = useState<SortField | null>('name');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  // id -> réplicas alvo do rascunho de escala rápida; some quando o stream
  // confirma o valor (ou quando o POST falha).
  const [quickDrafts, setQuickDrafts] = useState<Record<string, number>>({});
  const quickTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  useEffect(() => {
    const timers = quickTimers.current;
    return () => Object.values(timers).forEach(clearTimeout);
  }, []);

  useEffect(() => {
    setQuickDrafts(drafts => {
      const confirmed = Object.keys(drafts).filter(id => {
        if (quickTimers.current[id]) return false;
        const live = services.find(s => s.id === id);
        return !live || live.replicas === drafts[id];
      });
      if (confirmed.length === 0) return drafts;
      const next = { ...drafts };
      confirmed.forEach(id => delete next[id]);
      return next;
    });
  }, [services]);

  const clearQuickDraft = (id: string): void => {
    setQuickDrafts(drafts => {
      if (!(id in drafts)) return drafts;
      const next = { ...drafts };
      delete next[id];
      return next;
    });
  };

  const bumpQuickScale = (s: ServiceInfo, delta: number): void => {
    if (!onQuickScale) return;
    const target = Math.max(0, (quickDrafts[s.id] ?? s.replicas) + delta);
    setQuickDrafts(drafts => ({ ...drafts, [s.id]: target }));
    clearTimeout(quickTimers.current[s.id]);
    quickTimers.current[s.id] = setTimeout(() => {
      delete quickTimers.current[s.id];
      if (target === s.replicas) {
        clearQuickDraft(s.id);
        return;
      }
      onQuickScale(s, target, s.replicas).catch((err: unknown) => {
        clearQuickDraft(s.id);
        toast(`Falha ao escalar ${s.name}: ${errorMessage(err)}`, {
          tone: 'danger',
        });
      });
    }, QUICK_SCALE_FLUSH_MS);
  };

  const filaOpts = useMemo(
    () => uniqSorted(services.map(s => s.stack)),
    [services]
  );
  const sistemaOpts = useMemo(
    () => uniqSorted(services.map(s => s.system)),
    [services]
  );
  const tipoOpts = useMemo(
    () => uniqSorted(services.map(s => s.type)),
    [services]
  );

  const list = useMemo(() => {
    let out = filterServices(services, filter, ipsByService);
    if (fila) out = out.filter(s => s.stack === fila);
    if (sistema) out = out.filter(s => s.system === sistema);
    if (tipo) out = out.filter(s => s.type === tipo);
    if (onlyScaled) out = out.filter(s => s.machines > 0);
    if (sortField) {
      const dir = sortDir === 'asc' ? 1 : -1;
      out = [...out].sort(
        (a, b) =>
          (a[sortField] ?? '').localeCompare(b[sortField] ?? '', 'pt-BR', {
            numeric: true,
            sensitivity: 'base',
          }) * dir
      );
    }
    return out;
  }, [
    services,
    filter,
    ipsByService,
    fila,
    sistema,
    tipo,
    onlyScaled,
    sortField,
    sortDir,
  ]);

  const toggleSort = (field: SortField): void => {
    if (sortField === field) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const sortHeader = (label: string, field: SortField): ReactNode => (
    <button
      type="button"
      onClick={() => toggleSort(field)}
      className="inline-flex items-center gap-0.5 uppercase tracking-[.08em] hover:text-text"
    >
      {label}
      {sortField === field &&
        (sortDir === 'asc' ? <ArrowUp size={10} /> : <ArrowDown size={10} />)}
    </button>
  );

  const allChecked = list.length > 0 && list.every(s => bulk.has(s.id));
  const handleToggleAll = (): void => {
    onToggleBulkAll(
      list.map(s => s.id),
      !allChecked
    );
  };

  const cols = onQuickScale ? [...COLS, QUICK_SCALE_COL] : COLS;
  const rowGridStyle: CSSProperties = {
    gridTemplateColumns: cols
      .map(c => (typeof c === 'number' ? `${c}px` : c))
      .join(' '),
  };

  return (
    <div className="flex flex-1 min-h-0 flex-col">
      <ListFilterBar
        filter={filter}
        onFilterChange={onFilterChange}
        placeholder="service_name ou IP macvlan… (regex aceito)"
        mono
        extra={
          <>
            <FilterSelect
              label="Fila"
              allLabel="todas"
              value={fila}
              options={filaOpts}
              onChange={setFila}
            />
            <FilterSelect
              label="Sistema"
              allLabel="todos"
              value={sistema}
              options={sistemaOpts}
              onChange={setSistema}
            />
            <FilterSelect
              label="Tipo"
              allLabel="todos"
              value={tipo}
              options={tipoOpts}
              onChange={setTipo}
            />
            <button
              type="button"
              onClick={() => setOnlyScaled(v => !v)}
              aria-pressed={onlyScaled}
              className={cn(
                'h-6 px-2 rounded border text-[10.5px] font-mono font-bold transition-colors',
                onlyScaled
                  ? 'border-[var(--fm-accent)] bg-accentSoft text-[var(--fm-accent)]'
                  : 'border-border bg-surfaceAlt text-textMute hover:border-borderStrong'
              )}
            >
              Só escaladas
            </button>
          </>
        }
      />
      <ListHeader
        cols={cols}
        labels={[
          <Checkbox
            key="all"
            checked={allChecked}
            onChange={handleToggleAll}
            aria-label="select all services"
          />,
          sortHeader('Serviço', 'name'),
          sortHeader('Fila', 'stack'),
          sortHeader('Sistema', 'system'),
          'MÁQ',
          'REPL',
          ...(onQuickScale ? ['ESCALA'] : []),
        ]}
      />
      <div
        className="flex-1 overflow-auto bg-surface"
        data-testid="service-list-body"
      >
        {list.length === 0 && (
          <div className="px-4 py-6 text-center text-[11.5px] text-textMute">
            Nenhum serviço corresponde aos filtros atuais.
          </div>
        )}
        {list.map(s => {
          const isSel = s.id === selectedId;
          const isChecked = bulk.has(s.id);
          return (
            <div
              key={s.id}
              role="row"
              data-testid={`service-row-${s.id}`}
              onClick={() => onSelect(isSel ? null : s.id)}
              style={rowGridStyle}
              className={cn(
                'grid items-center gap-2 cursor-pointer text-[11.5px]',
                'border-b border-borderSoft pr-[14px] pl-3',
                compact ? 'py-[5px]' : 'py-[7px]',
                isSel
                  ? 'bg-accentSoft border-l-2 border-l-[var(--fm-accent)]'
                  : isChecked
                    ? 'border-l-2 border-l-transparent bg-accentSoft/40 hover:bg-bg'
                    : 'border-l-2 border-l-transparent bg-transparent hover:bg-bg'
              )}
            >
              <Checkbox
                checked={isChecked}
                onChange={() => onToggleBulk(s.id)}
                stopPropagation
                aria-label={`select service ${s.name}`}
              />
              <div className="flex min-w-0 items-center gap-[7px]">
                {s.id in pendingScale ? (
                  <span
                    data-testid={`service-status-${s.id}`}
                    title={`Escalando ${pendingScale[s.id].from} → ${pendingScale[s.id].target}`}
                    className="inline-flex w-[64px] flex-shrink-0 items-center justify-center gap-0.5 rounded bg-accentSoft px-1 py-px font-mono text-[8.5px] font-bold tracking-[0.03em] text-[var(--fm-accent)]"
                  >
                    <Loader2 size={8} className="animate-spin" />
                    ESCALANDO
                  </span>
                ) : (
                  <span
                    data-testid={`service-status-${s.id}`}
                    className={cn(
                      'w-[64px] flex-shrink-0 rounded px-1 py-px text-center font-mono text-[8.5px] font-bold tracking-[0.03em]',
                      STATUS_BADGE[serviceStatus(s)].className
                    )}
                  >
                    {STATUS_BADGE[serviceStatus(s)].label}
                  </span>
                )}
                <span className="truncate font-mono text-[10.5px] text-text">
                  {s.name}
                </span>
              </div>
              <div className="truncate text-[11px] font-semibold text-text">
                {s.stack || '—'}
              </div>
              <div>
                <span className="font-mono text-[9.5px] font-bold px-1.5 py-px rounded bg-accentSoft text-[var(--fm-accent)]">
                  {s.system || '—'}
                </span>
              </div>
              <CellMono value={s.machines} right bold />
              <CellMono value={s.replicas} right color="text-textMute" />
              {onQuickScale && (
                <div
                  className="flex items-center justify-end"
                  onClick={e => e.stopPropagation()}
                >
                  <div
                    className={cn(
                      'flex items-center rounded border border-border bg-surfaceAlt',
                      s.id in quickDrafts && 'border-[var(--fm-accent)]'
                    )}
                  >
                    <button
                      type="button"
                      aria-label={`reduzir réplicas de ${s.name}`}
                      disabled={
                        !!pendingScale[s.id]?.sending ||
                        (quickDrafts[s.id] ?? s.replicas) <= 0
                      }
                      onClick={() => bumpQuickScale(s, -1)}
                      className="px-1.5 py-0.5 text-textMute hover:text-text disabled:opacity-30 disabled:hover:text-textMute"
                    >
                      <Minus size={11} />
                    </button>
                    <span
                      data-testid={`quick-scale-value-${s.id}`}
                      className={cn(
                        'min-w-[22px] text-center font-mono text-[10.5px] font-bold',
                        s.id in quickDrafts
                          ? 'text-[var(--fm-accent)]'
                          : 'text-text'
                      )}
                    >
                      {quickDrafts[s.id] ?? s.replicas}
                    </span>
                    <button
                      type="button"
                      aria-label={`aumentar réplicas de ${s.name}`}
                      disabled={!!pendingScale[s.id]?.sending}
                      onClick={() => bumpQuickScale(s, 1)}
                      className="px-1.5 py-0.5 text-textMute hover:text-text disabled:opacity-30 disabled:hover:text-textMute"
                    >
                      <Plus size={11} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

ServiceList.displayName = 'ServiceList';

export default ServiceList;
